import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  TextField,
  Typography,
  Select,
  MenuItem,
} from "@mui/material";
import CloseIcon from "mdi-material-ui/Close";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { axiosInstance } from "../../network/adapter";
import { ApiEndPoints } from "../../network/endpoints";
import * as yup from "yup";
import { toastError, toastSuccess } from "../../utils/utils";
import { CleaveTextOnlyInput } from "src/@core/components/cleave-components";

const validationSchema = yup.object().shape({
  name: yup.string().trim().required("Name is Required."),
  email: yup
    .string()
    .email("Invalid email format")
    .required("Email is Required."),
  role: yup
    .string()
    .required("Role is Required.")
    .test("is-number", "Role is Required.", (value) => {
      return value !== "" && !isNaN(Number(value));
    }),
});

const DialogSubAdmin = (props) => {
  const { mode, open, toggle, dataToEdit, onSuccess, type, title } = props;
  const [loading, setLoading] = useState(false);
  const [rolesList, setRolesList] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      role: "",
    },
    resolver: yupResolver(validationSchema),
    mode: "onChange",
  });

  // Fetch roles list
  const fetchRoles = () => {
    setRolesLoading(true);
    axiosInstance
      .get(ApiEndPoints.ROLES.list)
      .then((response) => {
        setRolesList(response.data.data?.roles || []);
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setRolesLoading(false);
      });
  };

  useEffect(() => {
    if (open) {
      setLoading(false);
      reset({
        name: dataToEdit?.name || "",
        email: dataToEdit?.email || "",
        role: dataToEdit?.role || "",
      });
      // Fetch roles when dialog opens
      fetchRoles();
    }
  }, [dataToEdit, mode, open, reset]);

  const onSubmit = (data) => {
    setLoading(true);

    const payload = {
      name: data.name,
      email: data.email,
      role: parseInt(data.role),
    };

    let apiInstance = null;
    if (mode === "edit") {
      apiInstance = axiosInstance.patch(
        ApiEndPoints.SUB_ADMIN.edit(dataToEdit.id),
        payload
      );
    } else {
      apiInstance = axiosInstance.post(ApiEndPoints.SUB_ADMIN.create, payload);
    }

    apiInstance
      .then((response) => response.data)
      .then((response) => {
        onSuccess();
        toastSuccess(response.message);
        toggle();
      })
      .catch((error) => {
        toastError(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={toggle}
        fullWidth
        maxWidth="sm"
        scroll="paper"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="fm-h6">
            {mode === "add" ? `Add Sub Admin` : `Edit Sub Admin`}
          </Typography>
          <IconButton
            aria-label="close"
            onClick={toggle}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ pb: 8, px: { sx: 8, sm: 15 }, pt: { xs: 8, sm: 12.5 } }}
        >
          <form id="subadmin-form" onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ mb: 4 }}>
              <FormControl fullWidth>
                <FormLabel required htmlFor="name" error={Boolean(errors.name)}>
                  Name
                </FormLabel>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      placeholder="Enter name"
                      onChange={onChange}
                      id="name"
                      value={value}
                      error={Boolean(errors.name)}
                      InputProps={{
                        inputComponent: CleaveTextOnlyInput,
                      }}
                    />
                  )}
                />
                {errors.name && (
                  <FormHelperText sx={{ color: "error.main" }}>
                    {errors.name.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box sx={{ mb: 4 }}>
              <FormControl fullWidth>
                <FormLabel
                  required
                  htmlFor="email"
                  error={Boolean(errors.email)}
                >
                  Email
                </FormLabel>
                <Controller
                  name="email"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      placeholder="Enter email"
                      onChange={onChange}
                      id="email"
                      value={value}
                      error={Boolean(errors.email)}
                      type="email"
                    />
                  )}
                />
                {errors.email && (
                  <FormHelperText sx={{ color: "error.main" }}>
                    {errors.email.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>

            <Box sx={{ mb: 4 }}>
              <FormControl fullWidth>
                <FormLabel
                  required
                  htmlFor="role"
                  error={Boolean(errors.role)}
                >
                  Role
                </FormLabel>
                <Controller
                  name="role"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Select
                      value={value}
                      onChange={onChange}
                      error={Boolean(errors.role)}
                      displayEmpty
                      disabled={rolesLoading}
                    >
                      <MenuItem value="" disabled>
                        {rolesLoading ? "Loading roles..." : "Select Role"}
                      </MenuItem>
                      {rolesList.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.role && (
                  <FormHelperText sx={{ color: "error.main" }}>
                    {errors.role.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Box>
          </form>
        </DialogContent>
        <DialogActions>
          <LoadingButton
            size="large"
            type="submit"
            form="subadmin-form"
            variant="contained"
            loading={loading}
          >
            Submit
          </LoadingButton>
          <Button size="large" variant="outlined" onClick={toggle}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DialogSubAdmin;
